import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  bootstrapCsrf,
  clearCsrfToken,
  fetchMe,
  loginRequest,
  logoutRequest,
  type SessionUser,
} from './api';

interface AuthContextValue {
  user: SessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (login: string, password: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  /** Re-fetch the current operator (used after change-password to clear mustChangePassword). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        if (me) void bootstrapCsrf();
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (login: string, password: string): Promise<SessionUser> => {
    const me = await loginRequest(login, password);
    setUser(me);
    return me;
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await logoutRequest();
    } catch {
      // Session may already be gone server-side — clear local state regardless.
    }
    clearCsrfToken();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    const me = await fetchMe();
    setUser(me);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        signIn,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
